/**
 * The three things every screen needs and nothing else.
 *
 * The system this replaces built HTML with template strings and forgot to
 * escape in enough places that a customer's name with an apostrophe in it
 * could break a page. `esc` is here so there is one obvious thing to reach
 * for, and `money` and `ago` are here because both were reimplemented a
 * dozen times each and drifted.
 */

/** Everything interpolated into markup goes through this. No exceptions. */
export const esc = (v: unknown): string =>
  String(v ?? "").replace(/[&<>"']/g, (c) =>
    ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c] as string);

export const money = (n: unknown): string =>
  `$${Number(n ?? 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/**
 * ago() WITH THE WORD ON THE END, because one of its answers already has
 * one.
 *
 * Found 2026-09-16 walking an authorization through: the job card read
 * "Authorized over the phone by Dana Whitfield for $623.25 just now ago".
 * ago() returns "just now" under a minute and every caller wrote
 * `${since(x)}` around it -- 34 of them -- so every record touched in the
 * last minute said "just now ago", on every screen, for every shop.
 *
 * The suffix belongs with the thing that knows whether it is needed.
 */
export function since(iso: string | null | undefined): string {
  const s = ago(iso);
  if (!s) return "";
  return s === "just now" ? s : `${s} ago`;
}

/** Rough, human, and deliberately not precise. "3 days" beats "3.4 days". */
export function ago(iso: string | null | undefined): string {
  if (!iso) return "";
  const then = Date.parse(iso);
  if (Number.isNaN(then)) return "";
  const mins = Math.round((Date.now() - then) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min`;

  /**
   * MINUTES, THEN THE HOUR WITH ITS MINUTES, THEN JUST HOURS.
   *
   * Anthony: "quotes sorts by 1 hour not minutes then hour would be
   * nicer."
   *
   * Rounding to whole hours threw away the only detail that matters on a
   * screen sorted by recency: a quote raised ninety minutes ago and one
   * raised two hours and twenty read as "2 hr" and "2 hr", so the order
   * looked arbitrary even though the sort was right. Worse, round() made
   * ninety minutes into "2 hr" -- older than it is.
   *
   * So the first six hours carry their minutes. After that the minutes
   * stop earning their place: nobody sorting a day's work needs to know
   * that something was nine hours and forty minutes ago.
   */
  const hrs = Math.floor(mins / 60);
  const rem = mins % 60;
  if (hrs < 6) return rem ? `${hrs} hr ${rem} min` : `${hrs} hr`;
  if (hrs < 24) return `${hrs} hr`;
  const days = Math.round(hrs / 24);
  if (days < 30) return `${days} day${days === 1 ? "" : "s"}`;
  const months = Math.round(days / 30);
  return `${months} month${months === 1 ? "" : "s"}`;
}

/** In the future, in the same voice. */
export function until(iso: string | null | undefined): string {
  if (!iso) return "";
  const mins = Math.round((Date.parse(iso) - Date.now()) / 60000);
  if (Number.isNaN(mins)) return "";
  if (mins < 0) return `${since(iso)}`;
  if (mins < 60) return `in ${mins} min`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `in ${hrs} hr`;
  const days = Math.round(hrs / 24);
  return days === 1 ? "tomorrow" : `in ${days} days`;
}

/**
 * WHERE A SCREEN PAINTS.
 *
 * It used to be `#app` and nothing else, which was right while `#app` was
 * the only place a screen could go. On staging the screens moved into a
 * panel at the edge (src/ui/shell.ts) so the middle could hold the day, and
 * thirty-five page files should not each have to know that.
 *
 * So `mount` asks. The router sets the target before it runs a screen, and
 * every page carries on calling `mount` exactly as it did.
 *
 * FALLS BACK TO `#app`, ALWAYS. A target that has been removed from the
 * document — the panel shut mid-flight by a fast second click — would
 * otherwise throw the screen away in silence, which is the one failure
 * shape this app tries hardest not to have.
 */
let targetId = "app";

export function setMountTarget(id: string): void {
  targetId = id;
}

export function mount(html: string): void {
  mountTo(targetId, html) || mountTo("app", html);
}

/**
 * Paint a NAMED place, whatever the router is currently pointing at. The
 * day screen in the middle uses this: it is not a screen you navigate to
 * any more, it is the thing the panel opens on top of, and it owns `#app`
 * whether or not something else is being routed at the same moment.
 *
 * Returns whether it found the element, which is what lets `mount` fall
 * back cleanly rather than dropping a screen on the floor.
 */
export function mountTo(id: string, html: string): boolean {
  const el = document.getElementById(id);
  if (!el) return false;
  el.innerHTML = html;
  el.scrollTop = 0;
  return true;
}

/**
 * The first sentence of what somebody typed.
 *
 * Written WITHOUT a regex lookbehind on purpose. `split(/(?<=[.!?])\s/)`
 * is the obvious way and it white-screened an iPad at the shop on 3
 * September: Safari did not support lookbehind until 16.4, and because it
 * is a SYNTAX error rather than a runtime one the entire bundle failed to
 * parse. Nothing rendered, and the sign-in form is drawn by that bundle,
 * so "blank screen and cannot log in" were the same fault.
 */
export function firstSentence(text: unknown): string {
  const line = String(text ?? "").split(String.fromCharCode(10))[0] ?? "";
  const m = line.match(/^.*?[.!?](\s|$)/);
  return (m ? m[0] : line).trim();
}
